import MediaToolTip from 'components/MediaTooltip'
import { useDispatch, useSelector } from 'react-redux'
import { cn, Checkbox, useIsMobile } from '@wellesley/ui'
import { Trash2, X } from 'lucide-react'
import { FileDesc, Upload } from '@wellesley/openapi'
import { FC, useMemo, useState } from 'react'
import { platformSettingsSlice } from '@wellesley/settings'
import {
  isExtremeCase,
  mapUpload,
  MediaUpload,
  selectUploadFile,
  MediaState,
  useCheckUpload,
  UploadItem,
} from '@wellesley/uploads'

import { Video } from '../Video'
import LazyImage from '../LazyImage'
import PreviewLabel from '../PreviewLabel'
import { VideoProps } from '../Video/VideoPanel/types'
import { PREVIEW_CONTROL, SMALL_PREVIEW_SIZE } from 'constants/ui'
import { OpenGalleryProps } from '../MediaGallery/types'
import SensitiveContentOverlay, {
  ContentOverlayProps,
} from '../SensitiveContentOverlay'
import {
  uploadsProgressSlice,
  UploadsProgressState,
} from '@wellesley/uploads/slice/progressSlice'
import { RootState } from '@wellesley/store'
import isUploadType from 'helpers/isUploadType'
import isNewUpload from '@wellesley/uploads/helpers/isNewUpload'
import UpdateMetaModal from '../feed/UpdateMetaModal'
import MediaActionsMenu from './MediaActionsMenu'

export type SelectItemProps = { id: string }

export type MediaItemProps = ContentOverlayProps & {
  upload: MediaUpload
  imgClass?: string
  isTooltip?: boolean
  videoClass?: string
  mediaHeight?: number
  isSelected?: boolean
  containerWidth?: number
  onClick?: (props: OpenGalleryProps) => void
  onSelect?: (props: SelectItemProps) => void
  removeMedia?: (id: string) => void
  updateMedia?: (upload: UploadItem, altText: string) => Promise<void>
  allowWithoutSize?: boolean
} & Pick<
    VideoProps,
    | 'inView'
    | 'sizes'
    | 'canPlayInModal'
    | 'canPlayInline'
    | 'isSmallPreview'
    | 'onClosePreview'
  >

type ItemProps = ContentOverlayProps &
  Pick<
    MediaItemProps,
    | 'imgClass'
    | 'videoClass'
    | 'onSelect'
    | 'removeMedia'
    | 'isSelected'
    | 'mediaHeight'
    | 'canPlayInline'
    | 'onClosePreview'
    | 'isSmallPreview'
    | 'canPlayInModal'
    | 'allowWithoutSize'
  > & {
    upload: Upload
    alt: string
    isImage: boolean
    uploadSrc: string
    uploadFile: FileDesc
    containerWidth: number
  }

type State = RootState & { uploadsProgress?: UploadsProgressState }

const COMMON_MEDIA_CLASS = 'block max-w-[100%] rounded-[6px]'
const SMALL_PREVIEW_CLASS =
  'w-[100px] h-[100px] max-h-[100px] max-w-[100px] object-cover'

export const MediaItem: FC<MediaItemProps> = ({
  upload: uploadFromProps,
  isSmallPreview,
  isTooltip = true,
  imgClass,
  videoClass,
  onClick,
  updateMedia,
  removeMedia,
  containerWidth = 600,
  allowWithoutSize,
  ...itemProps
}) => {
  const currentUpload = useMemo(
    () => mapUpload(uploadFromProps),
    [uploadFromProps],
  )

  const { upload, containerRef, isProcessing } = useCheckUpload({
    upload: currentUpload,
  })

  const dispatch = useDispatch()

  const alt = upload.meta?.name || ''

  const isImage = isUploadType({ source: upload, type: 'IMAGE' })
  const isVideo = isUploadType({ source: upload, type: 'VIDEO' })

  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  const allowedExtensions = isVideo ? videoExtensions : undefined

  const previewUploadFile = useMemo(
    () =>
      selectUploadFile({
        upload,
        allowedExtensions,
        size: SMALL_PREVIEW_SIZE,
        allowWithoutSize,
      }),
    [upload, allowedExtensions, allowWithoutSize],
  )

  const bestFitUploadFile = useMemo(
    () =>
      selectUploadFile({
        upload,
        allowedExtensions,
        size: containerWidth,
        allowWithoutSize,
      }),
    [upload, allowedExtensions, containerWidth, allowWithoutSize],
  )

  const uploadFile = isSmallPreview ? previewUploadFile : bestFitUploadFile
  const uploadSrc = uploadFile?.uri || ''

  const uploadState = useSelector((state: State) =>
    uploadsProgressSlice.selectors.selectStateById(state, upload.uploadId),
  )

  const isNewUploadFile = isNewUpload(uploadSrc)

  const { progress, error, finished: isUploaded } = uploadState
  const isError = error !== null

  const handleProgressShown = () => {
    dispatch(uploadsProgressSlice.actions.setFinished({ id: upload.uploadId }))
  }

  const isMobile = useIsMobile()
  const [showEditAlt, setShowEditAlt] = useState(false)

  if ((!isImage && !isVideo) || !uploadSrc || !uploadFile) return null

  const showTooltip = isTooltip && !itemProps.isMediaHidden
  const preview = upload.files[0].uri || ''

  const hideAlt = (uploadFile.meta.width || 0) < SMALL_PREVIEW_SIZE || isError

  const canShowProgress = (isNewUploadFile && !isUploaded) || isProcessing

  return (
    <div
      onClick={() => onClick?.({ id: upload.uploadId, preview })}
      data-testid="mediaItem"
      data-id={PREVIEW_CONTROL}
      className="relative h-full"
      ref={containerRef}
    >
      {itemProps.onClosePreview && (
        <div
          data-testid="closePreview"
          className="size-l bg-alpha-black-30 absolute -right-[68px] -top-[68px]  flex cursor-pointer items-center justify-center rounded-full text-white"
          onClick={(e) => {
            e.stopPropagation()
            itemProps.onClosePreview?.()
          }}
        >
          <X size={24} color="white" />
        </div>
      )}
      {updateMedia && showEditAlt && (
        <UpdateMetaModal
          upload={upload}
          onClose={() => setShowEditAlt(false)}
          onUpdateMedia={updateMedia}
        />
      )}
      {canShowProgress && (
        <MediaState
          progress={progress}
          onCancel={removeMedia}
          errorMessage={error}
          onProgressShown={handleProgressShown}
          isProcessing={isProcessing && !isNewUpload}
          uploadId={upload.uploadId}
        />
      )}
      {removeMedia && (
        <PreviewLabel
          onClick={(e) => {
            e.stopPropagation()
            removeMedia(upload.uploadId)
          }}
          position="top-right"
          className="size-s rounded-full p-1.5"
        >
          <Trash2 size={16} />
        </PreviewLabel>
      )}
      {updateMedia && isMobile && (
        <MediaActionsMenu
          hideAlt={hideAlt}
          isMediaHidden={itemProps.isMediaHidden}
          contentWarning={itemProps.contentWarning}
          onAltClick={() => setShowEditAlt(true)}
          onToggleVisibility={(value) => itemProps.toggleShowMedia?.(value)}
        />
      )}
      {updateMedia && !isMobile && !hideAlt && (
        <PreviewLabel
          onClick={(e) => {
            e.stopPropagation()
            setShowEditAlt(true)
          }}
          isActive={showEditAlt}
          position="top-left"
          className="px-xxs rounded-full py-1.5"
        >
          ALT
        </PreviewLabel>
      )}
      <MediaToolTip title={showTooltip ? upload.meta?.altText || '' : ''}>
        <Item
          {...{
            alt,
            upload,
            isImage,
            imgClass,
            uploadFile,
            uploadSrc,
            removeMedia,
            isSmallPreview,
            containerWidth,
            allowWithoutSize,
          }}
          {...itemProps}
          imgClass={cn(imgClass, isSmallPreview ? SMALL_PREVIEW_CLASS : '')}
          videoClass={cn(videoClass, isSmallPreview ? SMALL_PREVIEW_CLASS : '')}
        />
      </MediaToolTip>
    </div>
  )
}

const Item: FC<ItemProps> = ({
  alt,
  isImage,
  upload,
  imgClass,
  uploadFile,
  uploadSrc,
  videoClass,
  isSelected,
  onSelect,
  removeMedia,
  mediaHeight,
  toggleShowMedia,
  contentWarning,
  containerWidth,
  isSmallPreview,
  allowWithoutSize,
  ...videoProps
}) => {
  const [canPlayVideo, setCanPlayVideo] = useState(
    !contentWarning && videoProps.canPlayInline,
  )

  const isSmallMedia = isSmallPreview || isExtremeCase(uploadFile)

  return (
    <div
      className={cn(
        'rounded-m relative h-full w-full',
        isSelected &&
          `after:block, after:bg-alpha-white-70 after:absolute after:right-0 after:top-0 after:size-[100%] after:content-['']`,
      )}
    >
      {contentWarning && (
        <SensitiveContentOverlay
          blurOnly={isSmallMedia}
          isSmallMedia={isSmallMedia}
          isMediaHidden={false}
          isRemoveButton={!!removeMedia}
          toggleShowMedia={(value) => {
            setCanPlayVideo(value)
            toggleShowMedia?.(value)
          }}
        />
      )}
      {onSelect && (
        <Checkbox
          onCheckedChange={() => onSelect({ id: upload.uploadId })}
          checked={isSelected}
          className="absolute right-[12px] top-[12px] z-[1]"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {isImage ? (
        <LazyImage
          src={uploadSrc}
          alt={alt}
          imgHeight={uploadFile.meta.height}
          mediaHeight={!isSmallPreview ? mediaHeight : undefined}
          blurHash={upload.meta?.blurhash}
          allowWithoutSize={allowWithoutSize}
          className={cn(
            COMMON_MEDIA_CLASS,
            imgClass,
            onSelect && 'pointer-events-none',
          )}
        />
      ) : (
        <Video
          {...videoProps}
          upload={upload}
          containerWidth={containerWidth}
          mediaHeight={mediaHeight}
          canPlayInline={canPlayVideo}
          isSmallPreview={isSmallPreview}
          className={cn(
            COMMON_MEDIA_CLASS,
            videoClass,
            onSelect && 'pointer-events-none',
          )}
        />
      )}
    </div>
  )
}
